import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addRemovedDomIdAsync,
  appendCustomContentAsync,
  clearBulletinPdfDraftMemoryCache,
  deleteBulletinPdfDraftAsync,
  flushBulletinPdfDraftNow,
  peekBulletinPdfDraft,
  readBulletinPdfDraftAsync,
  removeRemovedDomIdAsync,
  removeBulletinPdfFieldAsync,
  writeBulletinPdfFieldHtmlToLocal,
  writeBulletinPdfIntroductionEnabled,
} from 'pages/bulletin/pdf/utils/bulletinPdfFieldEditsStorage';
import {
  draftHasContent,
  normalizePdfTemplate,
} from 'pages/bulletin/pdf/utils/bulletinPdfIntroFields';
function selectDraftScope(getState) {
  const { bulletinId, pdfTemplate } = getState().bulletinPdfDraftReducer;
  return { bulletinId, pdfTemplate };
}

function hasDraftScope({ bulletinId }) {
  return bulletinId != null && bulletinId !== '';
}

async function runPersistOperation(getState, dispatch, persistFn) {
  const scope = selectDraftScope(getState);
  if (!hasDraftScope(scope)) return;

  dispatch({ type: 'bulletinPdfDraftReducer/saveStarted' });
  try {
    await persistFn(scope);
    dispatch({ type: 'bulletinPdfDraftReducer/saveSucceeded' });
  } catch (err) {
    dispatch({
      type: 'bulletinPdfDraftReducer/saveFailed',
      payload: err?.message || 'persist failed',
    });
    throw err;
  }
}

export const loadBulletinPdfDraft = createAsyncThunk(
  'bulletinPdfDraftReducer/loadBulletinPdfDraft',
  async (_, { getState, rejectWithValue }) => {
    const { bulletinId, pdfTemplate } = selectDraftScope(getState);
    if (!hasDraftScope({ bulletinId })) {
      return { phase: 'none', hasDraft: false, updatedAt: null };
    }

    try {
      const draft = await readBulletinPdfDraftAsync(bulletinId, pdfTemplate);
      const hasDraft = draftHasContent(draft);
      return {
        phase: hasDraft ? 'awaitingPageLoad' : 'none',
        hasDraft,
        updatedAt:
          hasDraft && draft?.updatedAt != null ? draft.updatedAt : null,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveBulletinPdfFieldHtml = createAsyncThunk(
  'bulletinPdfDraftReducer/saveBulletinPdfFieldHtml',
  async (
    {
      fieldId,
      html,
      bulletinId: bulletinIdOverride,
      template: templateOverride,
    },
    { getState, dispatch }
  ) => {
    if (fieldId == null) return;
    const storeScope = selectDraftScope(getState);
    const bulletinId = bulletinIdOverride ?? storeScope.bulletinId;
    const pdfTemplate = templateOverride ?? storeScope.pdfTemplate;
    if (!hasDraftScope({ bulletinId })) return;

    const nextHtml = html == null ? '' : String(html);
    dispatch({ type: 'bulletinPdfDraftReducer/saveStarted' });
    try {
      await writeBulletinPdfFieldHtmlToLocal(
        bulletinId,
        pdfTemplate,
        fieldId,
        nextHtml
      );
      dispatch({ type: 'bulletinPdfDraftReducer/saveSucceeded' });
    } catch (err) {
      dispatch({
        type: 'bulletinPdfDraftReducer/saveFailed',
        payload: err?.message || 'persist failed',
      });
      throw err;
    }
  }
);

export const removeBulletinPdfFieldHtml = createAsyncThunk(
  'bulletinPdfDraftReducer/removeBulletinPdfFieldHtml',
  async (
    { fieldId, bulletinId: bulletinIdOverride, template: templateOverride },
    { getState, dispatch }
  ) => {
    if (fieldId == null) return;
    const storeScope = selectDraftScope(getState);
    const bulletinId = bulletinIdOverride ?? storeScope.bulletinId;
    const pdfTemplate = templateOverride ?? storeScope.pdfTemplate;
    if (!hasDraftScope({ bulletinId })) return;

    dispatch({ type: 'bulletinPdfDraftReducer/saveStarted' });
    try {
      await removeBulletinPdfFieldAsync(bulletinId, pdfTemplate, fieldId);
      dispatch({ type: 'bulletinPdfDraftReducer/saveSucceeded' });
    } catch (err) {
      dispatch({
        type: 'bulletinPdfDraftReducer/saveFailed',
        payload: err?.message || 'persist failed',
      });
      throw err;
    }
  }
);

export const saveBulletinPdfRemovedDomId = createAsyncThunk(
  'bulletinPdfDraftReducer/saveBulletinPdfRemovedDomId',
  async (
    { domId, bulletinId: bulletinIdOverride, template: templateOverride },
    { getState, dispatch }
  ) => {
    if (domId == null) return;
    const storeScope = selectDraftScope(getState);
    const bulletinId = bulletinIdOverride ?? storeScope.bulletinId;
    const pdfTemplate = templateOverride ?? storeScope.pdfTemplate;
    if (!hasDraftScope({ bulletinId })) return;

    dispatch({ type: 'bulletinPdfDraftReducer/saveStarted' });
    try {
      await addRemovedDomIdAsync(bulletinId, pdfTemplate, domId);
      dispatch({ type: 'bulletinPdfDraftReducer/saveSucceeded' });
    } catch (err) {
      dispatch({
        type: 'bulletinPdfDraftReducer/saveFailed',
        payload: err?.message || 'persist failed',
      });
      throw err;
    }
  }
);

export const restoreBulletinPdfRemovedDomId = createAsyncThunk(
  'bulletinPdfDraftReducer/restoreBulletinPdfRemovedDomId',
  async ({ domId }, { getState, dispatch }) => {
    if (domId == null) return;
    await runPersistOperation(
      getState,
      dispatch,
      ({ bulletinId, pdfTemplate }) =>
        removeRemovedDomIdAsync(bulletinId, pdfTemplate, domId)
    );
  }
);

export const appendBulletinPdfCustomContent = createAsyncThunk(
  'bulletinPdfDraftReducer/appendBulletinPdfCustomContent',
  async ({ platform, item }, { getState, dispatch }) => {
    if (platform == null || platform === '' || !item) return;
    await runPersistOperation(
      getState,
      dispatch,
      ({ bulletinId, pdfTemplate }) =>
        appendCustomContentAsync(bulletinId, pdfTemplate, platform, item)
    );
  }
);

export const setBulletinPdfIntroductionEnabled = createAsyncThunk(
  'bulletinPdfDraftReducer/setBulletinPdfIntroductionEnabled',
  async ({ enabled }, { getState, dispatch }) => {
    const next = Boolean(enabled);
    dispatch({
      type: 'bulletinPdfDraftReducer/setIntroductionEnabledLocal',
      payload: next,
    });
    await runPersistOperation(
      getState,
      dispatch,
      ({ bulletinId, pdfTemplate }) =>
        writeBulletinPdfIntroductionEnabled(bulletinId, pdfTemplate, next)
    );
  }
);

export const flushBulletinPdfDraft = createAsyncThunk(
  'bulletinPdfDraftReducer/flushBulletinPdfDraft',
  async (_, { getState, dispatch }) => {
    const scope = selectDraftScope(getState);
    if (!hasDraftScope(scope)) return;

    dispatch({ type: 'bulletinPdfDraftReducer/saveStarted' });
    try {
      await flushBulletinPdfDraftNow(scope.bulletinId, scope.pdfTemplate);
      dispatch({
        type: 'bulletinPdfDraftReducer/saveSucceeded',
        payload: { bumpRevision: false },
      });
    } catch (err) {
      dispatch({
        type: 'bulletinPdfDraftReducer/saveFailed',
        payload: err?.message || 'persist failed',
      });
      throw err;
    }
  }
);

export const discardBulletinPdfDraft = createAsyncThunk(
  'bulletinPdfDraftReducer/discardBulletinPdfDraft',
  async (_, { getState }) => {
    const { bulletinId, pdfTemplate } = selectDraftScope(getState);
    if (!hasDraftScope({ bulletinId })) {
      return { cleared: true };
    }

    await deleteBulletinPdfDraftAsync(bulletinId, pdfTemplate);
    clearBulletinPdfDraftMemoryCache(bulletinId, pdfTemplate);
    return { cleared: true };
  }
);

export function syncBulletinPdfIntroductionEnabledFromCache() {
  return (dispatch, getState) => {
    const { bulletinId, pdfTemplate, phase } =
      getState().bulletinPdfDraftReducer;

    if (
      bulletinId == null ||
      bulletinId === '' ||
      phase === 'loading' ||
      phase === 'prompt' ||
      phase === 'awaitingPageLoad'
    ) {
      return;
    }

    const cached = peekBulletinPdfDraft(bulletinId, pdfTemplate);
    dispatch({
      type: 'bulletinPdfDraftReducer/setIntroductionEnabledLocal',
      payload: Boolean(cached?.introductionEnabled),
    });
  };
}

export function setBulletinPdfDraftScope({
  bulletinId,
  template,
  isPageReady,
}) {
  return {
    type: 'bulletinPdfDraftReducer/setScope',
    payload: {
      bulletinId,
      pdfTemplate: normalizePdfTemplate(template),
      isPageReady: Boolean(isPageReady),
    },
  };
}

export function resetBulletinPdfDraft() {
  return { type: 'bulletinPdfDraftReducer/reset' };
}

export function acceptBulletinPdfDraft() {
  return (dispatch, getState) => {
    const { bulletinId, pdfTemplate } = getState().bulletinPdfDraftReducer;
    if (hasDraftScope({ bulletinId })) {
      const cached = peekBulletinPdfDraft(bulletinId, pdfTemplate);
      dispatch({
        type: 'bulletinPdfDraftReducer/setIntroductionEnabledLocal',
        payload: Boolean(cached?.introductionEnabled),
      });
    }
    dispatch({ type: 'bulletinPdfDraftReducer/acceptDraft' });
  };
}

export function promptBulletinPdfDraftIfReady() {
  return (dispatch, getState) => {
    const { hasPendingDraft, isPageReady, phase } =
      getState().bulletinPdfDraftReducer;
    if (!hasPendingDraft || !isPageReady || phase !== 'awaitingPageLoad') {
      return;
    }
    dispatch({ type: 'bulletinPdfDraftReducer/setPhase', payload: 'prompt' });
  };
}
